import { InitialForm, useAdministrationModel } from './useAdministration.model';
import { Actions, Async, FSpread } from '@client/new/helpers/types';
import _ from 'lodash';
import auth from '@client/components/auth';
import {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormWatch,
} from 'react-hook-form';
import { Influencer } from '@playbook-technologies/common-enums';

export type AdministrationViewModelParams = {
  influencerId: number;
};

export type PayloadMap = {
  resetStripe: null;
  updateInfluencer: null;
  setTags: string[];
  setInsights: string[];
  setCustomizations: object;
};

export type AdministrationViewActions = Actions<PayloadMap>;
export type AdministrationViewDispatcher = FSpread<PayloadMap>;

export type AdministrationViewModel = {
  dispatch: AdministrationViewDispatcher;
  register: UseFormRegister<InitialForm>;
  watch: UseFormWatch<InitialForm>;
  control: Control<InitialForm, any, InitialForm>;
  submittingReset: boolean;
  submittingUpdate: boolean;
  isPaymentEnabled: boolean;
  paymentMethod: number | null;
  tags: string[];
  insights: string[];
  customizations: object;
  stripeUserId: string | null;
  isDeepLinkDisabled: boolean;
  errors: FieldErrors<InitialForm>;
  disableSuperAdminSections: boolean;
  showStripe: boolean;
  hidePublishedAt: boolean;
};

export const useAdministrationViewModel = (
  p: AdministrationViewModelParams,
): Async<AdministrationViewModel> => {
  const asyncModel = useAdministrationModel(p);

  if (asyncModel.type !== 'fetched') return asyncModel;

  const { watch } = asyncModel.value;
  const isPaymentEnabled = watch('isPaymentEnabled');
  const paymentMethod = watch('paymentMethod');
  const insights = watch('insights');
  const tags = watch('tags');
  const customizations = watch('customizations');
  const mode = watch('mode');

  const model = asyncModel.value;

  const dispatch: AdministrationViewDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as AdministrationViewActions;
    switch (action.type) {
      case 'resetStripe':
        return model.dispatch(action.type);
      case 'updateInfluencer':
        return model.dispatch(action.type);
      case 'setTags':
        return model.dispatch(action.type, action.payload);
      case 'setInsights':
        return model.dispatch(action.type, action.payload);
      case 'setCustomizations':
        return model.dispatch(action.type, action.payload);
    }
  };

  const showStripe = auth.hasStripePermission();
  const hidePublishedAt = mode === Influencer.Mode.Onboarding.value;

  return {
    type: 'fetched',
    value: {
      ...asyncModel.value,
      dispatch,
      isPaymentEnabled,
      paymentMethod,
      insights,
      tags,
      customizations,
      isDeepLinkDisabled: auth.isModerator(),
      disableSuperAdminSections: !auth.isAdministrator(),
      showStripe,
      hidePublishedAt,
    },
  };
};
